/**
 * Substitution Request Service
 *
 * Supabase persistence layer for substitution_requests records.
 * All reads are org-scoped via RLS; all writes explicitly include
 * organization_id from getCurrentOrganizationId().
 *
 * Reference pattern: src/services/lead.service.ts
 */

import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type {
  SubstitutionRequest,
  SubstitutionReason,
  SubstitutionCompatibility,
  SubstitutionCustomerImpact,
  SubstitutionStatus,
} from "@/data/VendorPOContext";

// ---------------------------------------------------------------------------
// Mapper — DB row (snake_case) → TS type (camelCase)
// ---------------------------------------------------------------------------
function fromRow(row: Record<string, unknown>): SubstitutionRequest {
  return {
    id:                          row.id as string,
    jobId:                       row.job_id as string,
    createdAt:                   row.created_at as string,
    createdBy:                   row.created_by as string,
    originalDescription:         row.original_description as string,
    proposedDescription:         row.proposed_description as string,
    reason:                      row.reason as SubstitutionReason,
    ...(row.original_material_reference != null
                                 ? { originalMaterialReference: row.original_material_reference as string } : {}),
    ...(row.proposed_material_reference != null
                                 ? { proposedMaterialReference: row.proposed_material_reference as string } : {}),
    compatibilityAssessment:     row.compatibility_assessment as SubstitutionCompatibility,
    ...(row.compatibility_notes != null
                                 ? { compatibilityNotes:        row.compatibility_notes as string }         : {}),
    customerImpact:              row.customer_impact as SubstitutionCustomerImpact,
    customerApprovalRequired:    row.customer_approval_required as boolean,
    status:                      row.status as SubstitutionStatus,
    ...(row.resolved_at != null  ? { resolvedAt:                row.resolved_at as string }                 : {}),
    ...(row.resolved_by != null  ? { resolvedBy:                row.resolved_by as string }                 : {}),
    ...(row.resolution_notes != null
                                 ? { resolutionNotes:           row.resolution_notes as string }            : {}),
    ...(row.canonical_po_id != null
                                 ? { canonicalPoId:             row.canonical_po_id as string }             : {}),
    ...(row.lineage_ambiguous != null
                                 ? { lineageAmbiguous:          row.lineage_ambiguous as boolean }          : {}),
    ...(row.lineage_ambiguous_reason != null
                                 ? { lineageAmbiguousReason:    row.lineage_ambiguous_reason as string }    : {}),
  };
}

// ---------------------------------------------------------------------------
// Mapper — TS type (camelCase) → DB row (snake_case)
// organization_id is injected separately in each write so it is never missed.
// ---------------------------------------------------------------------------
function toRow(req: SubstitutionRequest): Record<string, unknown> {
  return {
    id:                           req.id,
    job_id:                       req.jobId,
    created_at:                   req.createdAt,
    created_by:                   req.createdBy,
    original_description:         req.originalDescription,
    proposed_description:         req.proposedDescription,
    reason:                       req.reason,
    original_material_reference:  req.originalMaterialReference ?? null,
    proposed_material_reference:  req.proposedMaterialReference ?? null,
    compatibility_assessment:     req.compatibilityAssessment,
    compatibility_notes:          req.compatibilityNotes ?? null,
    customer_impact:              req.customerImpact,
    customer_approval_required:   req.customerApprovalRequired,
    status:                       req.status,
    resolved_at:                  req.resolvedAt ?? null,
    resolved_by:                  req.resolvedBy ?? null,
    resolution_notes:             req.resolutionNotes ?? null,
    canonical_po_id:              req.canonicalPoId ?? null,
    lineage_ambiguous:            req.lineageAmbiguous ?? null,
    lineage_ambiguous_reason:     req.lineageAmbiguousReason ?? null,
  };
}

function toPatchRow(patch: Partial<SubstitutionRequest>): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  if (patch.status                    !== undefined) row.status                       = patch.status;
  if (patch.resolvedAt                !== undefined) row.resolved_at                  = patch.resolvedAt;
  if (patch.resolvedBy                !== undefined) row.resolved_by                  = patch.resolvedBy;
  if (patch.resolutionNotes           !== undefined) row.resolution_notes             = patch.resolutionNotes;
  if (patch.compatibilityAssessment   !== undefined) row.compatibility_assessment     = patch.compatibilityAssessment;
  if (patch.compatibilityNotes        !== undefined) row.compatibility_notes          = patch.compatibilityNotes;
  if (patch.customerImpact            !== undefined) row.customer_impact              = patch.customerImpact;
  if (patch.customerApprovalRequired  !== undefined) row.customer_approval_required   = patch.customerApprovalRequired;
  if (patch.canonicalPoId             !== undefined) row.canonical_po_id              = patch.canonicalPoId;
  if (patch.lineageAmbiguous          !== undefined) row.lineage_ambiguous            = patch.lineageAmbiguous;
  if (patch.lineageAmbiguousReason    !== undefined) row.lineage_ambiguous_reason     = patch.lineageAmbiguousReason;
  return row;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------
export const SubstitutionRequestService = {
  /**
   * Fetch all substitution_requests for the current org.
   * RLS handles org scoping; organization_id filter is belt-and-suspenders.
   */
  async fetchAll(): Promise<SubstitutionRequest[]> {
    const organizationId = await getCurrentOrganizationId();
    const { data, error } = await supabase
      .from("substitution_requests")
      .select("*")
      .eq("organization_id", organizationId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("SubstitutionRequestService.fetchAll error:", error);
      return [];
    }
    return (data ?? []).map((row) => fromRow(row as Record<string, unknown>));
  },

  /**
   * Insert a new substitution request.
   * organization_id is always injected here — never rely on the caller.
   */
  async insert(req: SubstitutionRequest): Promise<void> {
    const organizationId = await getCurrentOrganizationId();
    const row = { ...toRow(req), organization_id: organizationId };
    const { error } = await supabase
      .from("substitution_requests")
      .insert(row);

    if (error) {
      console.error("SubstitutionRequestService.insert error:", error);
      throw error;
    }
  },

  /**
   * Patch an existing substitution request (e.g. approve / reject / supersede).
   * organization_id is included in the patch so RLS WITH CHECK passes.
   */
  async update(id: string, patch: Partial<SubstitutionRequest>): Promise<void> {
    const organizationId = await getCurrentOrganizationId();
    const row = { ...toPatchRow(patch), organization_id: organizationId };
    const { error } = await supabase
      .from("substitution_requests")
      .update(row)
      .eq("id", id)
      .eq("organization_id", organizationId);

    if (error) {
      console.error("SubstitutionRequestService.update error:", error);
      throw error;
    }
  },
};
